// Deterministic timeline shared by the preview and both export formats.
const copy = value => JSON.parse(JSON.stringify(value));
const distance = (a, b) => Math.hypot((a.x-b.x)*525, (a.y-b.y)*488);
const mix = (a,b,t) => ({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t});
const clamp = t => Math.max(0,Math.min(1,t));

export function curvePoint(curve,t){
  const {a,b,c1,c2}=curve;
  if(c2 && c1){
    const u=1-t;
    return {x:u*u*u*a.x+3*u*u*t*c1.x+3*u*t*t*c2.x+t*t*t*b.x,y:u*u*u*a.y+3*u*u*t*c1.y+3*u*t*t*c2.y+t*t*t*b.y};
  }
  if(c1) return mix(mix(a,c1,t),mix(c1,b,t),t);
  return mix(a,b,t);
}

function route(curve,start,end){
  const c={...copy(curve),a:{x:start.x,y:start.y},b:{x:end.x,y:end.y}};
  // Arc-length lookup keeps a curved route at the same speed as a straight one.
  const samples=[{t:0,length:0}];
  let length=0,previous=c.a;
  for(let i=1;i<=64;i++){
    const p=curvePoint(c,i/64);
    length+=distance(previous,p); samples.push({t:i/64,length});previous=p;
  }
  return {curve:c,samples,length};
}

function onRoute(path,t){
  const wanted=clamp(t)*path.length;
  const index=path.samples.findIndex(sample=>sample.length>=wanted);
  if(index<=0) return {...path.curve.a};
  const a=path.samples[index-1],b=path.samples[index];
  return curvePoint(path.curve,a.t+(b.t-a.t)*(wanted-a.length)/(b.length-a.length||1));
}

function playersIn(frame){
  const counts=new Map();
  return (frame.objects||[]).filter(o=>o?.type==='icon' && /^(?:[1-5]c?|x[1-5])\.svg$/i.test(o.icon||'') && Number.isFinite(o.x) && Number.isFinite(o.y)).map(o=>{
    const identity=o.icon.replace(/c\.svg$/i,'.svg').replace(/\.svg$/i,'');
    const n=counts.get(identity)||0; counts.set(identity,n+1);
    return {...copy(o),key:`${identity}:${n}`,label:identity,hasBall:/^[1-5]c\.svg$/i.test(o.icon)};
  });
}

function nearest(players,point,max=90){
  return players.map(p=>({p,d:distance(p,point)})).filter(v=>v.d<=max).sort((a,b)=>a.d-b.d)[0]?.p||null;
}

export function buildAnimation(frames,{tipForArrow=arrow=>arrow.curve.b}={}){
  const segments=[];let duration=0,previous=null,unmatched=0,inferredBall=false;
  function add(players,holder,ball,moves=[],pass=null,seconds=0.35,frameNumber=1){
    segments.push({start:duration,duration:seconds,players:copy(players),holder,ball:ball?{...ball}:null,moves,pass,frameNumber});
    duration+=seconds;
  }
  for(let frameIndex=0;frameIndex<(frames||[]).length;frameIndex++){
    const frame=frames[frameIndex],players=playersIn(frame);
    if(!players.length)continue;
    const frameNumber=frameIndex+1;
    const arrows=(frame.objects||[]).filter(o=>o?.type==='arrow' && o.curve?.a && o.curve?.b && [o.curve.a,o.curve.b,o.curve.c1,o.curve.c2].filter(Boolean).every(p=>Number.isFinite(p.x)&&Number.isFinite(p.y))).map(copy);
    let holder=players.find(p=>p.hasBall)?.key||null;
    if(!holder && previous && players.some(p=>p.key===previous.holder))holder=previous.holder;
    if(!holder && !previous){
      const pass=arrows.find(a=>a.style==='dash');
      const inferred=pass && nearest(players.filter(p=>!p.label.startsWith('x')),pass.curve.a);
      if(inferred){holder=inferred.key;inferredBall=true;}
    }
    let ball=holder?null:(previous?.ball||null);
    if(previous){
      const origins=players.map(p=>({...p,...(previous.players.find(old=>old.key===p.key)?{x:previous.players.find(old=>old.key===p.key).x,y:previous.players.find(old=>old.key===p.key).y}:{})}));
      const moves=players.map(p=>({key:p.key,path:route({a:p,b:p},origins.find(o=>o.key===p.key),p)}));
      add(origins,holder,ball,moves,null,0.35,frameNumber);
    }
    add(players,holder,ball,[],null,0.4,frameNumber);
    const pending=[...arrows];
    while(pending.length){
      const moves=[],used=new Set(),chosen=[];
      const candidates=[];
      for(const arrow of pending.filter(a=>a.style!=='dash')){
        for(const player of players){
          const d=distance(player,arrow.curve.a);
          if(d<=90)candidates.push({arrow,player,d});
        }
      }
      candidates.sort((a,b)=>a.d-b.d);
      for(const {arrow,player} of candidates){
        if(used.has(player.key)||chosen.includes(arrow))continue;
        used.add(player.key);chosen.push(arrow);
        moves.push({key:player.key,path:route(arrow.curve,player,tipForArrow(arrow)||arrow.curve.b)});
      }
      const owner=players.find(p=>p.key===holder);
      const passArrow=pending.filter(a=>a.style==='dash' && owner && distance(owner,a.curve.a)<=90).sort((a,b)=>distance(owner,a.curve.a)-distance(owner,b.curve.a))[0];
      let pass=null;
      if(passArrow){
        const positions=players.map(p=>({...p,...(moves.find(m=>m.key===p.key)?.path.curve.b||{})}));
        const receiver=nearest(positions.filter(p=>p.key!==holder&&!p.label.startsWith('x')),passArrow.curve.b);
        const endpoint=receiver||tipForArrow(passArrow)||passArrow.curve.b;
        pass={from:holder,to:receiver?.key||null,path:route(passArrow.curve,owner,endpoint)};
        chosen.push(passArrow);
      }
      if(!chosen.length){unmatched+=pending.length;break;}
      add(players,holder,ball,moves,pass,1.6,frameNumber);
      for(const move of moves)Object.assign(players.find(p=>p.key===move.key),move.path.curve.b);
      if(pass){holder=pass.to;ball=holder?null:{...pass.path.curve.b};}
      for(const arrow of chosen)pending.splice(pending.indexOf(arrow),1);
    }
    add(players,holder,ball,[],null,0.6,frameNumber);
    previous={players:copy(players),holder,ball};
  }
  return {segments,duration,unmatched,inferredBall};
}

export function sampleAnimation(timeline,time){
  if(!timeline.segments.length)return {players:[],ball:null,frameNumber:0};
  const segment=timeline.segments.find(s=>time<s.start+s.duration)||timeline.segments.at(-1);
  const t=clamp((time-segment.start)/segment.duration);
  const players=segment.players.map(player=>{
    const move=segment.moves.find(m=>m.key===player.key);
    return {...player,...(move?onRoute(move.path,t):{})};
  });
  let ball=segment.ball?{...segment.ball}:null;
  const owner=players.find(p=>p.key===segment.holder);
  if(owner)ball={x:owner.x,y:owner.y,attachedTo:owner.key};
  if(segment.pass){
    if(t===0 && owner)ball={x:owner.x,y:owner.y,attachedTo:owner.key};
    else if(t===1 && segment.pass.to){
      const receiver=players.find(p=>p.key===segment.pass.to);
      ball={x:receiver.x,y:receiver.y,attachedTo:receiver.key};
    }else ball={...onRoute(segment.pass.path,t),pass:segment.pass,t};
  }
  return {players,ball,frameNumber:segment.frameNumber};
}
